// src/hooks/usePromociones.js - Versión mejorada
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import API from '../utils/api';
import PromocionesService from '../services/PromocionesService';

/**
 * Hook mejorado que maneja las promociones aplicadas a un pedido
 * Incluye soporte mejorado para todos los tipos de promociones
 */
const usePromociones = (pedidoActual = [], promocionesDisponibles = []) => {
  // Estado para las promociones aplicadas al pedido
  const [promocionesAplicadas, setPromocionesAplicadas] = useState([]);

  // Estado para indicar si está calculando
  const [calculando, setCalculando] = useState(false);

  // Estado para almacenar detalles adicionales de promociones tipo 3
  const [detallesPromocionesT3, setDetallesPromocionesT3] = useState({});

  // Referencia para la última petición de promociones
  const ultimaPromise = useRef(null);

  // Obtener los detalles de las promociones tipo 3 al cambiar las promociones disponibles
  useEffect(() => {
    const obtenerDetallesPromocionesT3 = async () => {
      // Encontrar todas las promociones de tipo 3
      const promocionesT3 = promocionesDisponibles.filter(
        promo => (promo.tipo === '3' || promo.tipo === 3) && promo.activa !== false
      );

      if (promocionesT3.length === 0) return;

      const nuevosDetalles = { ...detallesPromocionesT3 };
      let hayNuevosDetalles = false;

      // Obtener detalles de cada promoción tipo 3 que no tengamos ya
      for (const promo of promocionesT3) {
        if (!nuevosDetalles[promo.idPromocion]) {
          try {
            const detallePromo = await API.promociones.getDetallePromocion(promo.idPromocion);

            if (detallePromo) {
              nuevosDetalles[promo.idPromocion] = {
                productosCobrar: detallePromo.productosCobrar || [],
                productosRegalar: detallePromo.productosRegalar || [],
                cantidad1: detallePromo.promocion?.cantidad1 ?? promo.cantidad1,
                cantidad2: detallePromo.promocion?.cantidad2 ?? promo.cantidad2
              };

              // También actualizar los atributos de la promoción original
              promo.productosCobrar = detallePromo.productosCobrar || [];
              promo.productosRegalar = detallePromo.productosRegalar || [];
              promo.cantidad1 = detallePromo.promocion?.cantidad1 ?? promo.cantidad1;
              promo.cantidad2 = detallePromo.promocion?.cantidad2 ?? promo.cantidad2;

              hayNuevosDetalles = true;
            }
          } catch (error) {
            console.error(`Error al obtener detalles de promoción ${promo.idPromocion}:`, error);
          }
        }
      }

      // Solo actualizar el estado si hay nuevos detalles
      if (hayNuevosDetalles) {
        setDetallesPromocionesT3(nuevosDetalles);
      }
    };

    obtenerDetallesPromocionesT3();
  }, [promocionesDisponibles, detallesPromocionesT3]);

  // Mezclar las promociones con sus detalles
  const promocionesEnriquecidas = useCallback(() => {
    return promocionesDisponibles.map(promo => {
      if ((promo.tipo === '3' || promo.tipo === 3) && detallesPromocionesT3[promo.idPromocion]) {
        const detalle = detallesPromocionesT3[promo.idPromocion];
        return {
          ...promo,
          productosCobrar: detalle.productosCobrar || [],
          productosRegalar: detalle.productosRegalar || [],
          cantidad1: detalle.cantidad1 ?? promo.cantidad1,
          cantidad2: detalle.cantidad2 ?? promo.cantidad2
        };
      }
      return promo;
    });
  }, [promocionesDisponibles, detallesPromocionesT3]);

  // Función para forzar el cálculo de promociones
  const recalcularPromociones = useCallback(async () => {
    if (pedidoActual.length === 0 || promocionesDisponibles.length === 0) {
      setPromocionesAplicadas([]);
      return;
    }

    setCalculando(true);

    // Crear un identificador único para esta petición
    const promiseId = Date.now();
    ultimaPromise.current = promiseId;

    try {
      // Obtener promociones enriquecidas con detalles
      const promocionesConDetalles = promocionesEnriquecidas();

      // Calcular promociones utilizando el servicio
      const promos = await PromocionesService.calcularPromociones(
        pedidoActual,
        promocionesConDetalles
      );

      // Solo actualizar si esta es la última petición enviada
      if (ultimaPromise.current === promiseId) {
        setPromocionesAplicadas(promos || []);
      }
    } catch (error) {
      console.error("Error al calcular promociones:", error);

      // Solo limpiar si esta es la última petición enviada
      if (ultimaPromise.current === promiseId) {
        setPromocionesAplicadas([]);
      }
    } finally {
      // Solo actualizar estado si esta es la última petición enviada
      if (ultimaPromise.current === promiseId) {
        setCalculando(false);
      }
    }
  }, [pedidoActual, promocionesEnriquecidas]);

  // Calcular promociones cuando cambie el pedido o los detalles de promociones
  // REGLA 4: Siempre recalcular promociones considerando todo el pedido, incluyendo productos guardados y nuevos
  useEffect(() => {
    // Solo calcular si hay productos en el pedido y promociones disponibles
    if (pedidoActual.length > 0 && promocionesDisponibles.length > 0) {
      // Recalcular promociones sin importar el estado (guardado o nuevo) de los productos
      recalcularPromociones();
    } else {
      // Si no hay productos o promociones, limpiar el estado
      setPromocionesAplicadas([]);
    }
  }, [pedidoActual, promocionesDisponibles, detallesPromocionesT3, recalcularPromociones]);

  /**
   * Verificar si un producto forma parte de una promoción tipo 3
   * @param {Object} producto - Producto a verificar
   * @returns {Object} Información sobre la promoción tipo 3 que aplica al producto
   */
  const verificarPromocionT3 = useCallback((producto) => {
    if (!producto) return { enPromocion: false };

    const idProducto = parseInt(producto.idProducto || producto.idPlatillo);

    for (const idPromocion in detallesPromocionesT3) {
      const detalle = detallesPromocionesT3[idPromocion];
      const promo = promocionesDisponibles.find(p => p.idPromocion === parseInt(idPromocion));

      if (!promo || (promo.tipo !== '3' && promo.tipo !== 3) || promo.activa === false) continue;

      // Verificar si el producto está en los productos a cobrar o regalar
      if ((detalle.productosCobrar && detalle.productosCobrar.includes(idProducto)) ||
        (detalle.productosRegalar && detalle.productosRegalar.includes(idProducto))) {
        return {
          enPromocion: true,
          promocion: {
            ...promo,
            productosCobrar: detalle.productosCobrar || [],
            productosRegalar: detalle.productosRegalar || []
          }
        };
      }
    }

    return { enPromocion: false };
  }, [detallesPromocionesT3, promocionesDisponibles]);

  /**
   * Verificar si un producto está en alguna promoción
   * @param {Object} producto - Producto a verificar
   * @returns {Object} Información de promociones aplicables al producto
   */
  const verificarPromociones = useCallback((producto) => {
    // Primero verificar promociones tipo 1 y 2 con el servicio
    const infoBasica = PromocionesService.verificarPromocionesProducto(producto, promocionesDisponibles);

    // Luego verificar si está en alguna promoción tipo 3
    const infoT3 = verificarPromocionT3(producto);

    // Si hay promoción de tipo 3, agregarla a la lista
    if (infoT3.enPromocion && infoT3.promocion) {
      return {
        enPromocion: true,
        promociones: [
          ...infoBasica.promociones,
          {
            idPromocion: infoT3.promocion.idPromocion,
            nombre: infoT3.promocion.nombre,
            tipo: '3',
            productosCobrar: infoT3.promocion.productosCobrar || [],
            productosRegalar: infoT3.promocion.productosRegalar || []
          }
        ]
      };
    }

    return infoBasica;
  }, [promocionesDisponibles, verificarPromocionT3]);

  /**
   * Calcular totales del pedido incluyendo descuentos
   */
  const calcularTotales = useCallback(() => {
    return PromocionesService.calcularTotalesPedido(pedidoActual, promocionesAplicadas);
  }, [pedidoActual, promocionesAplicadas]);

  /**
   * Verificar si un producto tiene promoción aplicada
   */
  const productoTienePromocion = useCallback((idPedidoDetalle) => {
    return PromocionesService.productoTienePromocion(idPedidoDetalle, promocionesAplicadas);
  }, [promocionesAplicadas]);

  /**
   * Obtener información detallada de la promoción aplicada a un producto
   */
  const getInfoPromocionProducto = useCallback((idPedidoDetalle) => {
    return PromocionesService.getInfoPromocionProducto(idPedidoDetalle, promocionesAplicadas);
  }, [promocionesAplicadas]);

  // Calcular el total de descuentos
  const totalDescuentos = useMemo(() => {
    return promocionesAplicadas.reduce(
      (total, promo) => total + (promo.descuento || 0),
      0
    );
  }, [promocionesAplicadas]);

  return {
    promocionesAplicadas,
    totalDescuentos,
    calculando,
    recalcularPromociones,
    verificarPromociones,
    calcularTotales,
    productoTienePromocion,
    getInfoPromocionProducto,
    detallesPromocionesT3
  };
};

export default usePromociones;